import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/api';

const AuthContext = createContext();

const API = `${BACKEND_URL}/api/admin`;

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Auto-logout after 1 hour of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkInactivity = setInterval(() => {
      const now = Date.now();
      const inactivityTime = now - lastActivity;
      const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds

      if (inactivityTime > oneHour) {
        console.log('Auto-logout due to inactivity');
        logout();
        alert('You have been logged out due to inactivity.');
      }
    }, 60000); // Check every 60 seconds

    return () => clearInterval(checkInactivity);
  }, [isAuthenticated, lastActivity]);

  // Track user activity
  useEffect(() => {
    if (!isAuthenticated) return;

    const updateActivity = () => {
      setLastActivity(Date.now());
    };

    // Listen to user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
      setLastActivity(Date.now());
    }
    setLoading(false);
  }, []);

  const login = async (email, password, totp_code = null) => {
    try {
      console.log('Login attempt:', email);
      console.log('API URL:', `${API}/login`);
      
      const loginData = { email, password };
      if (totp_code) {
        loginData.totp_code = totp_code;
      }
      
      const response = await axios.post(`${API}/login`, loginData);
      console.log('Login response:', response.data);
      
      const { access_token, force_password_change, require_2fa_setup } = response.data;
      
      localStorage.setItem('adminToken', access_token);
      if (force_password_change) {
        localStorage.setItem('forcePasswordChange', 'true');
      } else {
        localStorage.removeItem('forcePasswordChange');
      }
      if (require_2fa_setup) {
        localStorage.setItem('require2FASetup', 'true');
      } else {
        localStorage.removeItem('require2FASetup');
      }
      
      setToken(access_token);
      setIsAuthenticated(true);
      
      return { success: true, force_password_change, require_2fa_setup };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Login failed',
        requires_2fa: error.response?.data?.detail?.includes('2FA')
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('forcePasswordChange');
    localStorage.removeItem('require2FASetup');
    setToken(null);
    setIsAuthenticated(false);
  };

  const getAuthHeader = () => {
    return { Authorization: `Bearer ${token}` };
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, getAuthHeader, loading, token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};